import React, {useContext, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress, Dialog, DialogContent, Fade, LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Tooltip
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import {Stack} from "@mui/system";
import dayjs from "dayjs";
import Typography from "@mui/material/Typography";
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {PAContext} from "../../../Services/PAContext";
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import {PA_Crypto} from "../../Helpers/PA_Crypto";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {DocumentPasscodeModal} from "./DocumentPasscodeModal";


const parseCsv = (text) => {

    // CRITICAL CHECK: Ensure we received a string, not a Blob/File object.
    if (typeof text !== 'string') {
        console.error("parseCsv failed: Input data is not a string (received type:", typeof text, "). Expected file content.");
        return { type: 'error', data: 'Invalid data type for CSV parsing. Input must be file content (string), not a File object.' };
    }

    if (typeof Papa === 'undefined') return { type: 'error', data: 'PapaParse not available for CSV parsing. Check CDN script.' };

    try {
        // PapaParse works with the raw text string directly.
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        const rows = result.data;
        const headers = result.meta.fields;

        if (!headers || headers.length === 0) {
            return { type: 'error', data: 'Could not detect CSV headers. Check file format.' };
        }
        return { type: 'table', data: { headers, rows, title: 'CSV Data Preview' } };
    } catch (e) {
        return { type: 'text', data: { text: text, name: 'CSV Parsing Failed (showing raw text)' } };
    }
};
const parseExcel = (arrayBuffer) => {
    console.log("parseExcel : ", arrayBuffer);
    // Debugging: Log the byte length of the ArrayBuffer to confirm data receipt.
    console.log("parseExcel received ArrayBuffer. Byte Length:", arrayBuffer?.byteLength);

    if (typeof XLSX === 'undefined') return { type: 'error', data: 'SheetJS (XLSX) not available for Excel parsing. Check CDN script.' };

    // Check for empty or invalid buffer before attempting to read
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        return { type: 'error', data: 'File buffer is empty or invalid.' };
    }

    try {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        if (!sheet) return { type: 'error', data: 'Excel workbook is empty.' };

        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (!aoa || aoa.length === 0) {
            return { type: 'error', data: 'Sheet is empty.' };
        }

        const headers = aoa[0].map(h => String(h).trim() || 'Col');
        // We use range: 1 to skip the header row when converting to JSON objects
        const rows = XLSX.utils.sheet_to_json(sheet, { header: headers, range: 1 });

        return { type: 'table', data: { headers, rows, title: `Excel Data Preview (Sheet: ${sheetName})` } };

    } catch (e) {
        // Debugging: Log the full error object for better insight into the SheetJS failure
        console.error("XLSX Parsing Error:", e);
        return { type: 'error', data: `Failed to read Excel file. Error: ${e.message}` };
    }
};
const PdfRenderer = ({ dataUrl }) => {
    const [pdfLoading, setPdfLoading] = useState(true);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const renderPdf = async () => {
            if (!dataUrl || typeof pdfjsLib === 'undefined' || !containerRef.current) {
                setPdfLoading(false);
                if (containerRef.current) {
                    containerRef.current.innerHTML = `<p style="text-align:center; color: #d32f2f; padding: 10px;">PDF.js library not found. Cannot render preview.</p>`;
                }
                return;
            }

            setPdfLoading(true);
            containerRef.current.innerHTML = ''; // Clear container

            try {
                // pdfjsLib is assumed global
                const pdf = await pdfjsLib.getDocument({ url: dataUrl }).promise;
                const pagesToRender = Math.min(pdf.numPages, 3);

                for (let i = 1; i <= pagesToRender; i++) {
                    const page = await pdf.getPage(i);

                    // Use a reasonable default width or container width
                    const containerWidth = containerRef.current?.clientWidth || 300;
                    const initialViewport = page.getViewport({ scale: 1 });
                    const scale = Math.min(containerWidth / initialViewport.width, 2);
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    canvas.style.display = 'block';
                    canvas.style.margin = '5px auto';
                    canvas.style.maxWidth = '100%';
                    canvas.style.height = 'auto';
                    canvas.width = Math.floor(viewport.width);
                    canvas.height = Math.floor(viewport.height);

                    containerRef.current.appendChild(canvas);

                    const context = canvas.getContext('2d');
                    await page.render({ canvasContext: context, viewport }).promise;
                }

                if (pdf.numPages > 3 && containerRef.current) {
                    const p = document.createElement('p');
                    p.style.fontSize = '12px';
                    p.style.textAlign = 'center';
                    p.style.color = '#757575';
                    p.textContent = '(Preview limited to first 3 pages)';
                    containerRef.current.appendChild(p);
                }

            } catch (error) {
                console.error("Error rendering PDF:", error);
                if (containerRef.current) {
                    containerRef.current.innerHTML = `<p style="text-align:center; color: #d32f2f; padding: 10px;">Error loading PDF: ${error.message}</p>`;
                }
            } finally {
                setPdfLoading(false);
            }
        };

        renderPdf();
    }, [dataUrl]);

    return (
        <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 1 }}>
            {pdfLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100, color: 'primary.main' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption">Loading PDF Preview...</Typography>
                </Box>
            )}
            <div ref={containerRef}>
            </div>
        </Box>
    );
};
const PdfRendererUnlimited = ({ dataUrl }) => {
    const [pdfLoading, setPdfLoading] = useState(true);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const renderPdf = async () => {
            if (!dataUrl || typeof pdfjsLib === 'undefined' || !containerRef.current) {
                setPdfLoading(false);
                if (containerRef.current) {
                    containerRef.current.innerHTML = `<p style="text-align:center; color: #d32f2f; padding: 10px;">PDF.js library not found. Cannot render preview.</p>`;
                }
                return;
            }

            setPdfLoading(true);
            containerRef.current.innerHTML = ''; // Clear container

            try {
                // pdfjsLib is assumed global
                const pdf = await pdfjsLib.getDocument({ url: dataUrl }).promise;
                const pagesToRender = pdf.numPages;

                for (let i = 1; i <= pagesToRender; i++) {
                    const page = await pdf.getPage(i);

                    // Use a reasonable default width or container width
                    const containerWidth = containerRef.current?.clientWidth || 300;
                    const initialViewport = page.getViewport({ scale: 1 });
                    const scale = Math.min(containerWidth / initialViewport.width, 2);
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    canvas.style.display = 'block';
                    canvas.style.margin = '5px auto';
                    canvas.style.maxWidth = '100%';
                    canvas.style.height = 'auto';
                    canvas.width = Math.floor(viewport.width);
                    canvas.height = Math.floor(viewport.height);

                    containerRef.current.appendChild(canvas);

                    const context = canvas.getContext('2d');
                    await page.render({ canvasContext: context, viewport }).promise;
                }
            } catch (error) {
                console.error("Error rendering PDF:", error);
                if (containerRef.current) {
                    containerRef.current.innerHTML = `<p style="text-align:center; color: #d32f2f; padding: 10px;">Error loading PDF: ${error.message}</p>`;
                }
            } finally {
                setPdfLoading(false);
            }
        };

        renderPdf();
    }, [dataUrl]);

    return (
        <Box sx={{ overflowY: 'auto', p: 1 }}>
            {pdfLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100, color: 'primary.main' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption">Loading PDF Preview...</Typography>
                </Box>
            )}
            <div ref={containerRef}>
            </div>
        </Box>
    );
};
const TableRenderer = ({ headers, rows, title }) => {
    const limitedHeaders = headers.slice(0, 5);
    const limitedRows = rows.slice(0, 10);
    const isLimited = rows.length > 10 || headers.length > 5;

    return (
        <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>{title}</Typography>
            <Box sx={{ maxHeight: 350, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    <tr>
                        {limitedHeaders.map((h, i) => (
                            <th key={i} style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>{h}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {limitedRows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                            {limitedHeaders.map((h, j) => (
                                <td key={j} style={{ padding: '8px', color: '#424242', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row[h] !== undefined ? String(row[h]) : ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </Box>
            {isLimited && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>Preview limited to 10 rows and 5 columns.</Typography>
            )}
        </Box>
    );
};
const TableRendererUnlimited = ({ headers, rows, title }) => {

    return (
        <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>{title}</Typography>
            <Box sx={{ maxHeight: 350, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    <tr>
                        {headers.map((h, i) => (
                            <th key={i} style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>{h}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                            {headers.map((h, j) => (
                                <td key={j} style={{ padding: '8px', color: '#424242', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row[h] !== undefined ? String(row[h]) : ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
};

const DocumentViewer = ({ open, onClose, documentViewerData: file }) => {
    // 🎯 FIX 1: We 'alias' documentViewerData to 'file' so the rest of the code works
    if (!open || !file) return null;

    console.log("DocumentViewer Received File:", file);

    // Destructure properties
    const {
        name,
        url,
        data: rawData,
        file_type,
        data_category
    } = file;

    /**
     * LOGIC FIX:
     * We only accept the URL as 'decrypted' if it starts with 'blob:'.
     * This prevents the GCS path string from stopping the shimmer.
     */
    const decryptedUrl = url?.startsWith('blob:') ? url : (rawData?.startsWith('blob:') ? rawData : null);
    console.log("decryptedUrl:", decryptedUrl);

    // Determine type for the switch statement
    const type = data_category || (file_type?.split('/')[0]);
    console.log("type:", type);

    const contentBoxStyle = {
        p: 4,
        maxHeight: 'calc(100vh - 150px)',
        overflowY: 'auto',
        borderRadius: 2,
        bgcolor: '#ffffff',
        boxShadow: 24,
    };

    const renderContent = () => {
        // --- 1. THE DECRYPTION SHIMMER ---
        // If decryptedUrl is null (meaning it's still a GCS string path), show shimmer
        if (!decryptedUrl) {
            return (
                <Box sx={{ width: '80%', mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 10 }}>
                    <Box className="shimmer-container" sx={{
                        width: '200px', height: '280px',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: '16px',
                        position: 'relative', overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ width: '60%', height: '10px', bgcolor: 'rgba(255,255,255,0.1)', mb: 1, borderRadius: '4px' }} />
                            <Box sx={{ width: '40%', height: '8px', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '4px' }} />
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: 'white', letterSpacing: '2px', textTransform: 'uppercase' }}>
                            Decrypting
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', mt: 1 }}>
                            Unlocking secure file locally...
                        </Typography>
                        <LinearProgress color="primary" sx={{ width: '200px', mt: 3, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.1)' }} />
                    </Box>
                </Box>
            );
        }

        // --- 2. DECRYPTED RENDERING LOGIC ---
        switch (type) {
            case 'image':
                return (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <img
                            src={decryptedUrl}
                            alt={name}
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 200px)',
                                objectFit: 'contain',
                                borderRadius: '12px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                            }}
                        />
                    </Box>
                );

            case 'pdf':
                return <PdfRendererUnlimited dataUrl={decryptedUrl} />;

            case 'table':
                // Note: table data usually comes in the 'rawData' if it was pre-parsed,
                // but for encrypted files, it's usually in the decrypted blob.
                return <TableRendererUnlimited headers={file.headers} rows={file.rows} title={name} />;

            case 'html':
                return (
                    <Paper elevation={3} sx={{ ...contentBoxStyle, border: '1px solid #ff9800' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'warning.main' }}>
                            <WarningIcon sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">Security Warning: HTML Content</Typography>
                        </Box>
                        <Box
                            dangerouslySetInnerHTML={{ __html: decryptedUrl }}
                            sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff' }}
                        />
                    </Paper>
                );

            case 'text':
            case 'docx':
                return (
                    <Paper elevation={3} sx={{ ...contentBoxStyle }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="800">Raw Content Preview</Typography>
                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '13px', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            {decryptedUrl}
                        </Box>
                    </Paper>
                );

            default:
                return (
                    <Box sx={{ textAlign: 'center', p: 10, color: 'white' }}>
                        <DescriptionIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
                        <Typography variant="h6">Unsupported File Type</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.6 }}>Type: {type}</Typography>
                    </Box>
                );
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 400 }}
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    bgcolor: '#1a1a1a',
                    backgroundImage: 'none',
                    minHeight: '80vh'
                }
            }}
        >
            <Box sx={{
                p: 2, px: 3,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)'
            }}>
                <Box>
                    <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 900, display: 'block', lineHeight: 1 }}>
                        Vault Document
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        {name || 'Untitled Document'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.05)' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                {renderContent()}
            </DialogContent>

            <style>
                {`
                    @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                    .shimmer-container::after {
                        content: "";
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent);
                        animation: shimmer 1.5s infinite;
                    }
                `}
            </style>
        </Dialog>
    );
};


function DocumentCard({attendeeDocument, onEditDocument, openDeleteDialog}){

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage} = useContext(PAContext);
    const { Account } = dataArray;


    const isUploading = attendeeDocument.data === 'uploading...';
    const isDeleting = attendeeDocument.isDeleting === true;
    const isBusy = isUploading || isDeleting;

    const COLOR_EXPIRED = '#D32F2F';      // MUI Default Error/Red
    const COLOR_EXPIRES_SOON = '#ED6C02';  // MUI Default Warning/Orange
    const COLOR_NO_DATE = '#428ce2';
    const COLOR_VALID = 'green';

    // --- EXPIRATION LOGIC (moved out of JSX) ---
    const expiresRaw = attendeeDocument.expires?.toString().trim() ?? '';
    const hasExpiration = Boolean(expiresRaw);

    const now = dayjs();
    const expiresAt = hasExpiration ? dayjs(expiresRaw) : null;
    const hasValidExpiration = hasExpiration && expiresAt.isValid();
    const isExpired = hasValidExpiration && expiresAt.isBefore(now, 'day');
    const isExpiringSoon = hasValidExpiration && !isExpired && expiresAt.diff(now, 'day') <= 60;

    // Icon color based on state
    let expirationIconColor;
    let statusLabel = "";

    if (!hasValidExpiration) {
        expirationIconColor = COLOR_NO_DATE; // e.g., Gray
        statusLabel = "No Date";
    } else if (isExpired) {
        expirationIconColor = COLOR_EXPIRED; // e.g., Red
        statusLabel = `Expired ${expiresAt.format('DD MMM, YYYY')}`;
    } else if (isExpiringSoon) {
        expirationIconColor = COLOR_EXPIRES_SOON; // e.g., Orange/Yellow
        statusLabel = `Expires ${expiresAt.format('DD MMM, YYYY')}`;
    } else {
        expirationIconColor = COLOR_VALID; // e.g., Green
        statusLabel = `Expires ${expiresAt.format('DD MMM, YYYY')}`;
    }

    const expirationTooltipTitle = !hasValidExpiration
        ? 'No expiration date set for this document'
        : isExpired
            ? `This document EXPIRED on ${expiresAt.format('MMM D, YYYY')}`
            : `This document EXPIRES on ${expiresAt.format('MMM D, YYYY')}`;

    const getRelativeExpirationString = (expiresAt) => {
        const now = dayjs();
        const diffDays = expiresAt.diff(now, 'day');

        // If already expired, we don't show the "time until"
        if (diffDays < 0) return null;

        const years = expiresAt.diff(now, 'year');
        const months = expiresAt.diff(now.add(years, 'year'), 'month');
        const days = expiresAt.diff(now.add(years, 'year').add(months, 'month'), 'day');

        let parts = [];

        if (years > 0) {
            parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
            if (months > 0) parts.push(`${months} ${months === 1 ? 'mo' : 'mos'}`);
        } else if (months >= 3) {
            // More than 90 days: Show months and days
            parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
            if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
        } else {
            // Less than 90 days: Show weeks and days
            const weeks = Math.floor(diffDays / 7);
            const remainingDays = diffDays % 7;
            if (weeks > 0) parts.push(`${weeks} ${weeks === 1 ? 'week' : 'weeks'}`);
            if (remainingDays > 0) parts.push(`${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`);
            if (weeks === 0 && remainingDays === 0) return "Expires today";
        }

        return parts.join(', ');
    };
    // 🎯 Get the meaningful duration string
    const timeUntil = hasValidExpiration && !isExpired ? getRelativeExpirationString(expiresAt) : null;





    const [anchorDocumentListItemMenu, setAnchorDocumentListItemMenu] = useState(null);
    const handleDocumentListItemMenuOpenMenu = (event) => {
        setAnchorDocumentListItemMenu(event.currentTarget);
    };
    const handleDocumentListItemMenuClose = () => {
        setAnchorDocumentListItemMenu(null);
    };


    function renderPreview(){

        const _type = attendeeDocument.data_category;
        const _data = attendeeDocument.data;

        switch (_type) {
            case 'image':
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <img
                            src={_data}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: 350, borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                        />
                    </Box>
                );
            case 'pdf':
                return <PdfRenderer dataUrl={_data} />;
            case 'csv':
                const csv_result = parseCsv(_data);
                const csv_data = csv_result.data;
                return <TableRenderer headers={csv_data.headers} rows={csv_data.rows} title={csv_data.title} />;
            case 'xls':
                const xls_result = parseExcel(_data);
                const xls_data = xls_result.data;
                return <TableRenderer headers={xls_data.headers} rows={xls_data.rows} title={xls_data.title} />;
            case 'table':
                //return <TableRenderer headers={data.headers} rows={data.rows} title={data.title} />;
            case 'html':
                return (
                    <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa', overflow: 'auto' }}>
                        <Typography variant="caption" sx={{ color: 'error.main', mb: 1, display: 'block', fontWeight: 'bold', textAlign: 'center' }}>
                            Warning: HTML content is rendered directly and may be unstable or unsafe.
                        </Typography>
                        <Box
                            // DANGER: Rendering raw HTML content.
                            dangerouslySetInnerHTML={{ __html: _data.text }}
                        />
                    </Box>
                );
            case 'text':
                const limitedText = _data.text.substring(0, 5000) + (_data.text.length > 5000 ? '\n\n... (preview truncated)' : '');
                return (
                    <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa', maxHeight: 300, overflow: 'auto' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block', fontWeight: 'bold' }}>Text Preview ({_data.name})</Typography>
                        <Box component="pre" sx={{ fontSize: '10px', whiteSpace: 'pre-wrap', p: 1, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #f0f0f0', color: '#424242' }}>
                            {limitedText}
                        </Box>
                        {_data.text.length > 5000 && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>Showing first 5000 characters.</Typography>}
                    </Box>
                );
            case 'error':
                return (
                    <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error" gutterBottom>Parsing Error</Typography>
                        <Typography variant="caption" color="text.secondary">{_data}</Typography>
                    </Box>
                );
            case 'unsupported':
                return (
                    <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#e0f7fa', border: '1px solid #80deea', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#00bcd4' }} gutterBottom>Unsupported File Type</Typography>
                        <Typography variant="caption" color="text.secondary">No inline preview available for: {_data.name} ({_data.type || 'Unknown'}).</Typography>
                    </Box>
                );
            case 'none':
            default:
                return (
                    <Box sx={{ height: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'text.disabled' }}>
                        <Typography variant="body2">No file selected for preview.</Typography>
                    </Box>
                );
        }
    }

    const handleFooterItemClick = (_item) => {
        console.log("handleFooterItemClick : ", _item);

        if (_item === "edit"){
            onEditDocument(attendeeDocument);
        } else if (_item === "download") {
            downloadDocument(attendeeDocument);  //might need to be decrypted before downloading; passcode required
        } else if (_item === "delete"){
            openDeleteDialog(attendeeDocument);
        }

        //close the menu
        handleDocumentListItemMenuClose();
    };

    // --- helpers: mime/ext inference ---
    function mimeFromDoc(doc) {
        if (doc?.file_type) return doc.file_type;

        switch (doc?.data_category) {
            case "csv":  return "text/csv";
            case "html": return "text/html";
            case "json": return "application/json";
            case "pdf":  return "application/pdf";
            case "text":
            case "txt":  return "text/plain";
            default:     return "application/octet-stream";
        }
    }
    function extFromMime(mime) {
        if (!mime) return "bin";
        if (mime.includes("pdf")) return "pdf";
        if (mime.includes("json")) return "json";
        if (mime.includes("csv")) return "csv";
        if (mime.includes("html")) return "html";
        if (mime.includes("text")) return "txt";
        return "bin";
    }
    function buildFilename(doc, mime) {
        if (doc?.file_name) return doc.file_name;
        const base = (doc?.name || "document").replace(/[\/\\:?*"<>|]/g, "_");
        return `${base}.${extFromMime(mime)}`;
    }
    // --- helpers: convert attendeeDocument.data -> Blob ---
    function base64ToBlob(b64, mime) {
        const binStr = atob(b64);
        const len = binStr.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i);
        return new Blob([bytes], { type: mime || "application/octet-stream" });
    }
    async function dataToBlob(data, mime) {
        if (!data) return new Blob([], { type: mime || "application/octet-stream" });

        // Data URL (data:<mime>;base64,...)
        if (typeof data === "string" && data.startsWith("data:")) {
            const res = await fetch(data);
            return await res.blob();
        }

        // Base64 string (best-effort check). If you tag base64 explicitly elsewhere, use that flag instead.
        if (typeof data === "string" && !data.startsWith("data:")) {
            try {
                return base64ToBlob(data, mime);
            } catch {
                // If it wasn't valid base64, fallback to plain text
                return new Blob([data], { type: mime || "text/plain" });
            }
        }

        // Raw bytes
        if (data instanceof ArrayBuffer) {
            return new Blob([new Uint8Array(data)], { type: mime || "application/octet-stream" });
        }
        if (data instanceof Uint8Array) {
            return new Blob([data], { type: mime || "application/octet-stream" });
        }

        // Fallback: stringify objects
        if (typeof data === "object") {
            return new Blob([JSON.stringify(data, null, 2)], { type: mime || "application/json" });
        }

        // Fallback: plain text
        return new Blob([String(data)], { type: mime || "text/plain" });
    }

    // --- main: trigger download ---
    async function downloadDocument(doc) {
        if (doc.passcode_needed){

        }
        const mime = mimeFromDoc(doc);
        const filename = buildFilename(doc, mime);
        const blob = await dataToBlob(doc?.data, mime);
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }


    const [isPasscodeModalOpen, setIsPasscodeModalOpen] = useState(false);
    const [activeDocToUnlock, setActiveDocToUnlock] = useState(null);

    const handleView = () => {
        if (attendeeDocument.passcode_needed) {
            setActiveDocToUnlock(attendeeDocument);
            setIsPasscodeModalOpen(true);
        } else {
            // Standard view logic (fetch signed URL and open viewer)
            requestDocumentView(attendeeDocument);
        }
    };
    const handlePasscodeSubmit = (passcode) => {
        setIsPasscodeModalOpen(false);
        // Proceed to download and decrypt
        requestDocumentView(activeDocToUnlock, passcode);
    };
    const requestDocumentView = async (doc, passcode = null) => {

        console.log("requestDocumentView for Document : ", doc);

        try {
            //so at this point we either have a passcode if required or no passcode
            //so now we get the signed url that we need to get the document data

            if (attendeeDocument.data !== "" && PA_GlobalObject.Planning.status !== 10) {

                console.log("sending request for Signed URL");

                sendSocketMessage({
                    action: attendeeDocument.status === 1 //account
                        ? "E_requestSignedUrlForAccountDocumentForGCS"
                        : "E_requestSignedUrlForEventDocumentForGCS",

                    event_id: PA_GlobalObject.Planning.id,
                    document_id: attendeeDocument.id,
                    gcs_path: attendeeDocument.data
                });

                //now we open the viewer while we wait for the server to respond with the signed url
                setDocumentViewerData({
                    id: attendeeDocument.id,
                    url: null, // This triggers the "Shimmer" state
                    name: attendeeDocument.name,
                    file_type: attendeeDocument.file_type,
                    pending_passcode: passcode,
                    status: attendeeDocument.status
                });
                setDocumentViewerOpen(true);

            } else {
                console.log("Can't get SignedUrl bc missing attendeeDocument.data");
            }

        } catch (err) {
            console.error("View Request Error:", err);
            alert(err.message || "An error occurred while opening the document.");
        }
    };


    //this is for the larger document viewer
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerData, setDocumentViewerData] = useState({
        id: '',
        url: '',
        name: '',
        file_type: '',
        pending_passcode: '',
        status: ''
    });
    const handleCloseDocumentViewer = (() => {
        // If the URL is a local blob, release it from memory
        if (documentViewerData.url && documentViewerData.url.startsWith('blob:')) {
            URL.revokeObjectURL(documentViewerData.url);
        }

        setDocumentViewerOpen(false);

        // Reset the data so the useEffect triggers correctly on the next document click
        setDocumentViewerData({
            id: '',
            url: '',
            name: '',
            file_type: '',
            pending_passcode: '',
            status: ''
        });
    });

    useEffect(() => {
        // 1. Guard: Only proceed if we have an ID and haven't already generated a local Blob URL
        if (!documentViewerData.id || documentViewerData.url) return;

        // 2. Lookup Source A: Account Documents
        const accountDoc = Account?.documents?.find(d =>
            String(d.id) === String(documentViewerData.id)
        );

        // 3. Lookup Source B: Event Documents (from Global State)
        const eventDoc = PA_GlobalObject?.Planning?.documents?.find(d =>
            String(d.id) === String(documentViewerData.id)
        );

        // 4. Determine which one we found
        const docToProcess = accountDoc || eventDoc;

        // 5. Trigger decryption if the signed URL is present
        if (docToProcess && docToProcess.signedUrl) {
            console.log(`Signed URL detected for ${docToProcess.key_scope || 'account'} document. Starting decryption...`);
            processDocumentDownload(docToProcess);
        }

    }, [
        Account.documents,
        PA_GlobalObject.Planning.documents, // 🎯 Critical: Trigger when event docs update
        documentViewerData.id,
        documentViewerData.url
    ]);
    const processDocumentDownload = async (doc) => {
        console.log("processDocumentDownload : ", doc);
        console.log("attendeeDocument.signedUrl : ", doc.signedUrl);
        console.log("attendeeDocument.urlTimestamp: ", doc.urlTimestamp);

        // --- EXPIRY CHECK ---
        const urlCreatedAt = doc.urlTimestamp; // 1776768370241
        const now = Date.now();

        // Define your TTL (Time To Live).
        // Example: If your backend generates URLs valid for 1 hour, use 3600000ms.
        // We subtract a 5-minute "buffer" (300,000ms) to be safe.
        const urlMaxAge = 3600000; // 1 Hour in milliseconds
        const expirationThreshold = urlCreatedAt + urlMaxAge - 300000;

        if (now > expirationThreshold) {
            console.warn("Signed URL is expired or near expiry. Blocking fetch.");
//            alert("This download link has expired. Please refresh your document list to get a new link.");
            // Optional: Trigger your refresh logic here automatically
            return;
        }

        try {
            // 1. Fetch from GCS
            const response = await fetch(doc.signedUrl);

            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("xml")) {
                const errorText = await response.text();
                console.error("GCS Error Response:", errorText);
                throw new Error("The download link has expired. Please refresh the list.");
            }

            if (!response.ok) throw new Error("Could not fetch from storage");

            console.log("fetch response : ", response);

            const encryptedBuffer = await response.arrayBuffer();
            console.log("encryptedBuffer length : ", encryptedBuffer.byteLength);

            /*
                !!! if the encryptedBuffer length is 1729 then it's going to fail the decryption
                it's an error message of some sort rather than storage document
                find out what the problem is
             */


            // 1. Determine the context-based fallback secret
            // Status 2 is your "Event" flag, Status 1 is "Account"
            const contextSecret = (doc.status === 2)
                ? PA_GlobalObject.Planning.id   // Event Secret
                : Account.id;                   // Account Secret

            // 2. Final Secret Selection
            // If passcode is needed, use the one provided by the viewer modal.
            // Otherwise, use the context secret derived above.
            const secret = doc.passcode_needed
                ? documentViewerData.pending_passcode
                : contextSecret;
            console.log("Using Secret:", secret);

            // 🎯 CRITICAL FIX: Ensure IV and Salt are Uint8Arrays
            // This prevents "OperationError" if they come back as standard arrays or objects

            console.log("attendeeDocument.iv.data:", doc.iv.data);
            console.log("attendeeDocument.iv:", doc.iv);
            console.log("attendeeDocument.salt.data:", doc.salt.data);
            console.log("attendeeDocument.salt:", doc.salt);

            const iv = new Uint8Array(doc.iv.data || doc.iv);
            const salt = new Uint8Array(doc.salt.data || doc.salt);

            // We simulate a fresh encryption to see what the IV/Salt produces
            const testKey = await PA_Crypto.getKey(secret, salt);
            console.log("Test Key Derived Successfully");

            // 🎯 CHECKPOINT:
            // If the following logic works with a tiny dummy string,
            // then your PA_Crypto logic is fine, and the GCS file is the problem.
            try {
                const dummyData = new TextEncoder().encode("test");
                const dummyEnc = await window.crypto.subtle.encrypt({name: "AES-GCM", iv: iv}, testKey, dummyData);
                const dummyDec = await window.crypto.subtle.decrypt({name: "AES-GCM", iv: iv}, testKey, dummyEnc);
                console.log("Internal Crypto Test: PASSED ✅ (Logic is sound)");
            } catch(e) {
                console.error("Internal Crypto Test: FAILED ❌ (Key derivation is inconsistent)");
            }



            // Debug: Ensure lengths are correct (IV should be 12, Salt should be 16)
            console.log(`IV Length: ${iv.length}, Salt Length: ${salt.length}`);

            // 3. Decrypt
            const decryptedBuffer = await PA_Crypto.decrypt(
                new Uint8Array(encryptedBuffer),
                secret,
                iv,
                salt
            );

            // 4. Create Blob
            const finalBlob = new Blob([decryptedBuffer], { type: doc.file_type });
            const localUrl = URL.createObjectURL(finalBlob);
            console.log("Generated Decrypted Blob URL:", localUrl);

            // 5. Update the Viewer State
            setDocumentViewerData(prev => ({
                ...prev,
                url: localUrl,
                data: localUrl, // Sync both for the component logic
                data_category: doc.data_category || (doc.file_type.split('/')[0])
            }));

        } catch (err) {
            console.error("Decryption Error:", err);
            // If decryption fails, it's usually a bad passcode or bad IV/Salt
//            alert("Decryption failed. The file may be corrupted or the passcode is incorrect.");
            handleCloseDocumentViewer();
        }
    };


    return (
        <>
            <Card
                elevation={0}
                style={{
                    width: '100%',
                    marginBottom: '16px',
                    borderRadius: '16px',
                    border: isDeleting
                        ? '1px solid #d32f2f'
                        : isUploading ? '1px dashed #151269' : '1px solid #e0e4e8',
                    position: 'relative',
                    overflow: 'visible',
                    backgroundColor: isDeleting ? '#fff5f5' : isUploading ? '#fbfcfe' : '#fff',
                    transition: 'all 0.3s ease',
                    opacity: isBusy ? 0.85 : 1,
                    pointerEvents: isBusy ? 'none' : 'auto',
                    animation: isDeleting ? 'pulse-red 2s infinite' : isUploading ? 'pulse 2s infinite' : 'none'
                }}
            >
                {/* 🔴/🟢/🔵 STATUS ACCENT BAR */}
                <Box sx={{
                    position: 'absolute',
                    left: 0,
                    top: '15%',
                    bottom: '15%',
                    width: '4px',
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: isDeleting ? '#d32f2f' : isUploading ? '#151269' : expirationIconColor,
                    boxShadow: isBusy ? 'none' : `2px 0 10px ${expirationIconColor}33`,
                    transition: 'background-color 0.3s ease'
                }} />

                <Box sx={{ p: 2, pl: 2.5 }}>
                    {/* HEADER AREA */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ maxWidth: '80%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* ICON SLOT: Spinner vs File Icon */}
                            {isBusy ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }}>
                                    <CircularProgress size={20} thickness={6} sx={{ color: isDeleting ? '#d32f2f' : '#151269' }} />
                                </Box>
                            ) : (
                                <Box sx={{ width: 32, height: 32, bgcolor: '#f5f7f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="icon-file-text" style={{ fontSize: '16px', color: '#151269' }} />
                                </Box>
                            )}

                            <Box>
                                <Typography variant="caption" sx={{
                                    color: isDeleting ? '#d32f2f' : '#888',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    fontSize: '9px',
                                    letterSpacing: '0.5px'
                                }}>
                                    {isDeleting ? 'Removing from Vault...' : isUploading ? 'Securing Document...' : (attendeeDocument.data_category || 'Document')}
                                </Typography>
                                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: isDeleting ? '#d32f2f' : '#151269', lineHeight: 1.2, mt: 0.2 }}>
                                    {attendeeDocument.name}
                                </Typography>
                            </Box>
                        </Box>

                        {/* HIDE MENU DURING UPLOAD OR DELETE */}
                        {!isBusy && (Account.id === attendeeDocument.created_by || attendeeDocument.status === 1) && (
                            <IconButton
                                size="small"
                                onClick={handleDocumentListItemMenuOpenMenu}
                                sx={{ mt: -0.5, mr: -0.5, bgcolor: '#f5f7f9' }}
                            >
                                <i className="icon-menu2" style={{ fontSize: "14px", color: '#151269' }} />
                            </IconButton>
                        )}
                    </Box>

                    {/* NOTES */}
                    <Box sx={{ mt: 1.5 }}>
                        {attendeeDocument.notes && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#666',
                                    fontSize: '12px',
                                    lineHeight: 1.4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                {attendeeDocument.notes}
                            </Typography>
                        )}
                    </Box>

                    {/* STATUS TEXT */}
                    <Box sx={{ mt: 1.5 }}>
                        {isDeleting ? (
                            <Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '12px', fontWeight: 600, fontStyle: 'italic' }}>
                                Permanent deletion in progress...
                            </Typography>
                        ) : isUploading ? (
                            <Typography variant="body2" sx={{ color: '#151269', fontSize: '12px', fontWeight: 600, fontStyle: 'italic' }}>
                                Preparing encryption and transmitting to Vault...
                            </Typography>
                        ) : null
                        }
                    </Box>

                    {/* EXPIRATION WARNING (Hide during busy) */}
                    {/* ALWAYS SHOW EXPIRATION STATUS */}
                    {!isBusy && (
                        <Tooltip title={expirationTooltipTitle} arrow placement="top">
                            <Box sx={{
                                mt: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'help',
                                opacity: hasValidExpiration ? 1 : 0.5 // Dim the icon slightly if no date is set
                            }}>
                                <i
                                    className="icon-alarm"
                                    style={{
                                        fontSize: '14px',
                                        color: expirationIconColor
                                    }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            color: expirationIconColor,
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {statusLabel}
                                    </Typography>

                                    {/* 🎯 Sub-text for remaining time */}
                                    {timeUntil && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                lineHeight: 1.2
                                            }}
                                        >
                                            ({timeUntil} remaining)
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Tooltip>
                    )}

                    <Box sx={{ height: '1px', bgcolor: isDeleting ? '#fed7d7' : '#f0f0f0', my: 2 }} />

                    {/* ACTIONS & SECURITY FOOTER */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" gap={1.5}>
                            <Tooltip title={attendeeDocument.private ? 'Private' : 'Public'}>
                                <Box sx={{ color: attendeeDocument.private ? 'green' : '#ff6e1a', opacity: isBusy ? 0.3 : 1 }}>
                                    <i className={attendeeDocument.private ? 'icon-eye-cross' : 'icon-eye'} style={{ fontSize: '16px' }} />
                                </Box>
                            </Tooltip>

                            <Tooltip title={attendeeDocument.passcode_needed ? 'Passcode Locked' : 'No Passcode'}>
                                <Box sx={{ color: attendeeDocument.passcode_needed ? 'green' : '#ff6e1a', opacity: isBusy ? 0.3 : 1 }}>
                                    <i className={attendeeDocument.passcode_needed ? 'icon-lock' : 'icon-unlock'} style={{ fontSize: '16px' }} />
                                </Box>
                            </Tooltip>

                            <Tooltip title={isDeleting ? "Deleting..." : "Secure File"}>
                                <Box sx={{ color: isDeleting ? '#d32f2f' : isUploading ? '#ff6e1a' : 'green' }}>
                                    <i className={isDeleting ? "icon-bin" : isUploading ? "icon-spinner11" : "icon-shield-check"} style={{ fontSize: '16px' }} />
                                </Box>
                            </Tooltip>
                        </Stack>

                        <Button
                            onClick={handleView}
                            variant="contained"
                            size="small"
                            disabled={isBusy}
                            disableElevation
                            sx={{
                                borderRadius: '8px',
                                bgcolor: isDeleting ? '#feb2b2' : isUploading ? '#ccc' : '#151269',
                                fontWeight: 800,
                                px: 2,
                                '&:hover': { bgcolor: isDeleting ? '#feb2b2' : '#0a084d' }
                            }}
                        >
                            {isDeleting ? 'DELETING...' : isUploading ? 'SAVING...' : 'VIEW'}
                        </Button>
                    </Box>
                </Box>

                {/* CONTEXT MENU */}
                <Menu
                    anchorEl={anchorDocumentListItemMenu}
                    open={Boolean(anchorDocumentListItemMenu)}
                    onClose={handleDocumentListItemMenuClose}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid #eee',
                            minWidth: '150px'
                        }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={() => handleFooterItemClick('edit')} sx={{ fontSize: '13px', fontWeight: 600 }}>
                        <ListItemIcon sx={{ minWidth: '30px !important' }}><i className="icon-pencil" style={{ color: '#151269' }} /></ListItemIcon>
                        Edit
                    </MenuItem>
                    <MenuItem onClick={() => handleFooterItemClick('download')} sx={{ fontSize: '13px', fontWeight: 600 }}>
                        <ListItemIcon sx={{ minWidth: '30px !important' }}><i className="icon-download" style={{ color: '#151269' }} /></ListItemIcon>
                        Download
                    </MenuItem>
                    <Box sx={{ height: '1px', bgcolor: '#eee', my: 0.5 }} />
                    <MenuItem onClick={() => handleFooterItemClick('delete')} sx={{ fontSize: '13px', fontWeight: 600, color: '#d32f2f' }}>
                        <ListItemIcon sx={{ minWidth: '30px !important' }}><i className="icon-trash" style={{ color: '#d32f2f' }} /></ListItemIcon>
                        Delete
                    </MenuItem>
                </Menu>
            </Card>

            {/* CSS for Pulse Animation */}
            <style>
                {`
                @keyframes pulse {
                    0% { opacity: 0.85; }
                    50% { opacity: 0.6; }
                    100% { opacity: 0.85; }
                }
            `}
            </style>

            <DocumentPasscodeModal
                open={isPasscodeModalOpen}
                onClose={() => setIsPasscodeModalOpen(false)}
                onConfirm={handlePasscodeSubmit}
                docName={activeDocToUnlock ? activeDocToUnlock.name : ''}
            />

            {!isUploading && (
                <DocumentViewer
                    open={documentViewerOpen}
                    onClose={handleCloseDocumentViewer}
                    documentViewerData={documentViewerData} // 🎯 This must match the name in the {} of the component!
                />
            )}
        </>
    );
}

export default DocumentCard;